/* PGEducationHistory.js
   PG-specific Education & Academic History component.
   Section A: High School Details (aligned with UG EducationHistoryUndergraduate.js)
   Section B: Bachelor's degree + Academic Awards + Coventry Alumni
   Zero modifications to existing EducationHistoryUndergraduate.js or EducationHistoryGraduate.js
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'
import Loading from './Loading'

// Degree options fetched from API (CODE_DEGREE table)
// Returns {Id: DegreeId, value: LONG_DESC}

const ACADEMIC_AWARD_OPTIONS = [
  { Id: 'Diploma', value: 'Diploma' },
  { Id: 'Certificate', value: 'Certificate' },
  { Id: 'Masters', value: "Master's Degree" },
  { Id: 'ProfQualification', value: 'Professional Qualification' },
  { Id: 'Other', value: 'Other' }
]

const COV_ANSWER_OPTIONS = [
  { Id: 'Yes', value: 'Yes' },
  { Id: 'No', value: 'No' }
]

const ACADEMIC_AWARD_ANSWER = [
  { Id: 'Yes', value: 'Yes' },
  { Id: 'No', value: 'No' }
]

export default class PGEducationHistory extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      /* -------- Section A: High School -------- */
      highSchoolCertificate: '',
      highSchoolEndDate: '',
      highSchoolInstitutionId: '',     // selected from highSchoolNameData
      highSchoolInstitutionName: '',   // free-text when 'Other'
      educationGradeHS: '',            // populated dropdown based on certificate
      /* -------- Section B: Bachelor -------- */
      bachelorUniversity: '',
      bachelorUniversityName: '',
      bachelorDegree: '',
      bachelorFieldOfStudy: '',
      bachelorYearOfGrad: '',
      /* Academic Award */
      hasAcademicAward: '',   // 'Yes' | 'No'
      academicAwards: [],     // [{type, institution, year, description}]
      /* Coventry Alumni */
      covGraduate: '',        // 'Yes' | 'No'
      coventryId: '',
      /* Lookup data */
      certificateData: [],       // HS certificates
      highSchoolNameData: [],    // HS school names (separate from certificates)
      educationGradeData: [],    // grades per selected certificate
      universityData: [],        // bachelor university list
      degreeData: []             // bachelor degree types from CODE_DEGREE
    }
    this.addAward = this.addAward.bind(this)
    this.removeAward = this.removeAward.bind(this)
    this.buildAndPublish = this.buildAndPublish.bind(this)
    this.fetchGradesForCertificate = this.fetchGradesForCertificate.bind(this)
  }

  componentDidMount() {
    /* Fetch high school certificates */
    fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetSchoolCurriculum`)
      .then(res => res.json())
      .then(res => {
        this.setState({ certificateData: res || [] })
      })
      /* Fetch high school NAMES (separate endpoint — same as UG) */
      .then(() =>
        fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetSchoolName`)
      )
      .then(res => res.json())
      .then(res => {
        this.setState({ highSchoolNameData: res || [] })
      })
      /* Fetch bachelor universities */
      .then(() =>
        fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetUniversitiesName`)
      )
      .then(res => res.json())
      .then(res => {
        this.setState({ universityData: res || [] })
      })
      /* Fetch degree types from CODE_DEGREE */
      .then(() =>
        fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetDegrees`)
      )
      .then(res => res.json())
      .then(res => {
        this.setState({ degreeData: res || [], loading: false })
        /* Populate from saved props THEN publish so Main.js state is in sync */
        this.populateFromProps()
      })
      .catch(() => this.setState({ loading: false }))
  }

  fetchGradesForCertificate(curriculumId) {
    if (!curriculumId) {
      this.setState({ educationGradeData: [], educationGradeHS: '' })
      return
    }
    fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetSchoolGrade?curriculumId=${curriculumId}`)
      .then(res => res.json())
      .then(res => {
        this.setState({ educationGradeData: res || [] })
      })
      .catch(() => { })
  }

  populateFromProps() {
    const { educationHistory, data } = this.props
    const pgData = data || {}

    const bachelorState = {
      bachelorUniversity: pgData.bachelorUniversity || '',
      bachelorUniversityName: pgData.bachelorUniversityName || '',
      bachelorDegree: pgData.bachelorDegree || '',
      bachelorFieldOfStudy: pgData.bachelorFieldOfStudy || '',
      bachelorYearOfGrad: pgData.bachelorYearOfGrad || '',
      covGraduate: pgData.covGraduate || '',
      coventryId: pgData.coventryId || '',
      hasAcademicAward: pgData.hasAcademicAward || '',
      academicAwards: pgData.academicAwards || []
    }

    if (educationHistory && educationHistory.length > 0) {
      const hs = educationHistory[0]
      const hsState = {
        highSchoolCertificate: hs.CurriculumId || '',
        highSchoolEndDate: hs.EndDate ? hs.EndDate.split('T')[0] : '',
        highSchoolInstitutionId: hs.InstitutionId || '',
        highSchoolInstitutionName: hs.InstitutionName || '',
        educationGradeHS: hs.EducationGrade || ''
      }
      /* Fetch grades for the pre-filled certificate before publishing */
      if (hs.CurriculumId) {
        fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetSchoolGrade?curriculumId=${hs.CurriculumId}`)
          .then(res => res.json())
          .then(res => {
            this.setState({ educationGradeData: res || [] })
          })
          .catch(() => {})
      }
      this.setState({ ...bachelorState, ...hsState }, this.buildAndPublish)
    } else {
      this.setState(bachelorState, this.buildAndPublish)
    }
  }

  buildAndPublish () {
    const s = this.state
    const data = {
      /* High school section stays compatible with existing educationHistorys shape */
      highSchoolSection: {
        highSchoolCertificate: s.highSchoolCertificate,
        endDate: s.highSchoolEndDate,
        highSchoolName: s.highSchoolInstitutionId,
        institutionName: s.highSchoolInstitutionName,
        educationGrade: s.educationGradeHS
      },
      /* Bachelor section */
      bachelorUniversity: s.bachelorUniversity,
      bachelorUniversityName: s.bachelorUniversityName,
      bachelorDegree: s.bachelorDegree,
      bachelorFieldOfStudy: s.bachelorFieldOfStudy,
      bachelorYearOfGrad: s.bachelorYearOfGrad,
      hasAcademicAward: s.hasAcademicAward,
      academicAwards: s.academicAwards,
      covGraduate: s.covGraduate,
      coventryId: s.coventryId
    }
    this.props.collectState('PG_EH', data)
  }

  addAward () {
    const newAwards = [...this.state.academicAwards, { type: '', institution: '', year: '', description: '' }]
    this.setState({ academicAwards: newAwards }, this.buildAndPublish)
  }

  removeAward (index) {
    const newAwards = this.state.academicAwards.filter((_, i) => i !== index)
    this.setState({ academicAwards: newAwards }, this.buildAndPublish)
  }

  updateAward (index, field, val) {
    const newAwards = this.state.academicAwards.map((a, i) =>
      i === index ? { ...a, [field]: val } : a
    )
    this.setState({ academicAwards: newAwards }, this.buildAndPublish)
  }

  render () {
    const {
      loading, certificateData, highSchoolNameData, educationGradeData, universityData,
      highSchoolCertificate, highSchoolEndDate,
      highSchoolInstitutionId, highSchoolInstitutionName, educationGradeHS,
      bachelorUniversity, bachelorUniversityName, bachelorDegree,
      bachelorFieldOfStudy, bachelorYearOfGrad,
      hasAcademicAward, academicAwards, covGraduate, coventryId
    } = this.state

    const { error } = this.props
    /* 'Other' school ID in the UG system is '8' */
    const isOtherSchool = String(highSchoolInstitutionId) === '8' || String(highSchoolInstitutionId).toLowerCase() === 'other'

    if (loading) return <Loading />

    return (
      <div>
        {/* ======================= SECTION A: HIGH SCHOOL ======================= */}
        <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 16, marginBottom: 20 }}>
          <h5 style={{ color: '#3168CE', marginBottom: 12 }}>High School Details</h5>

          {/* High School Certificate — drives the grade dropdown */}
          <InputRow
            rowName='High School Certificate'
            name='select'
            value={highSchoolCertificate}
            data={certificateData}
            required='*'
            errMsg={error.highSchoolCertificate || ''}
            onChange={(val) => {
              this.setState({ highSchoolCertificate: val, educationGradeHS: '' }, this.buildAndPublish)
              this.fetchGradesForCertificate(val)
            }}
          />

          {/* End Year */}
          <InputRow
            rowName='Graduation / End Year'
            name='date'
            value={highSchoolEndDate}
            required='*'
            errMsg={error.highSchoolEndDate || ''}
            onChange={(val) => {
              this.setState({ highSchoolEndDate: val }, this.buildAndPublish)
            }}
          />

          {/* High School Name — uses highSchoolNameData (separate from certificates) */}
          <InputRow
            rowName='High School Name'
            name={highSchoolNameData.length > 0 ? 'select' : 'text'}
            value={highSchoolInstitutionId}
            data={highSchoolNameData}
            required='*'
            errMsg={error.highSchoolInstitutionId || ''}
            onChange={(val) => {
              this.setState(
                { highSchoolInstitutionId: val, highSchoolInstitutionName: '' },
                this.buildAndPublish
              )
            }}
          />

          {/* Institution name free-text — only when 'Other' school is selected (same as UG) */}
          {isOtherSchool && (
            <InputRow
              rowName='High School Name (Specify)'
              name='text'
              value={highSchoolInstitutionName}
              required='*'
              errMsg={error.highSchoolInstitutionName || ''}
              onChange={(val) => {
                this.setState({ highSchoolInstitutionName: val }, this.buildAndPublish)
              }}
            />
          )}

          {/* Grade — populated dropdown based on selected certificate (same as UG) */}
          <InputRow
            rowName='Grade'
            name={educationGradeData.length > 0 ? 'select' : 'text'}
            value={educationGradeHS}
            data={educationGradeData}
            required='*'
            errMsg={error.educationGradeHS || ''}
            onChange={(val) => {
              this.setState({ educationGradeHS: val }, this.buildAndPublish)
            }}
          />
        </div>

        {/* ======================= SECTION B: BACHELOR ======================= */}
        <div style={{ marginBottom: 20 }}>
          <h5 style={{ color: '#3168CE', marginBottom: 12 }}>
            University (Bachelor&apos;s degree awarded from)
          </h5>

          <InputRow
            rowName='University'
            name={universityData.length > 0 ? 'select' : 'text'}
            value={bachelorUniversity}
            data={universityData}
            required='*'
            errMsg={error.bachelorUniversity || ''}
            onChange={(val) => {
              this.setState(
                { bachelorUniversity: val, bachelorUniversityName: '' },
                this.buildAndPublish
              )
            }}
          />

          {(String(bachelorUniversity) === 'other' || String(bachelorUniversity).toLowerCase() === 'other') && (
            <InputRow
              rowName='University Name (Specify)'
              name='text'
              value={bachelorUniversityName}
              required='*'
              errMsg={error.bachelorUniversityName || ''}
              onChange={(val) => {
                this.setState({ bachelorUniversityName: val }, this.buildAndPublish)
              }}
            />
          )}

          <InputRow
            rowName='Degree / Certificate'
            name='select'
            value={bachelorDegree}
            data={this.state.degreeData}
            required='*'
            errMsg={error.bachelorDegree || ''}
            onChange={(val) => {
              this.setState({ bachelorDegree: val }, this.buildAndPublish)
            }}
          />

          <InputRow
            rowName='Field of Study (Faculty / Major)'
            name='text'
            value={bachelorFieldOfStudy}
            required='*'
            errMsg={error.bachelorFieldOfStudy || ''}
            onChange={(val) => {
              this.setState({ bachelorFieldOfStudy: val }, this.buildAndPublish)
            }}
          />

          <InputRow
            rowName='Year of Graduation'
            name='date'
            value={bachelorYearOfGrad}
            required='*'
            errMsg={error.bachelorYearOfGrad || ''}
            onChange={(val) => {
              this.setState({ bachelorYearOfGrad: val }, this.buildAndPublish)
            }}
          />

          {/* ---------- Academic Award ---------- */}
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
              Have you completed any diplomas, certificates, master's degrees, or professional qualifications?
            </p>
            <InputRow
              rowName='Academic Award'
              name='select'
              value={hasAcademicAward}
              data={ACADEMIC_AWARD_ANSWER}
              required='*'
              errMsg={error.hasAcademicAward || ''}
              onChange={(val) => {
                this.setState(
                  { hasAcademicAward: val, academicAwards: val === 'Yes' && academicAwards.length === 0 ? [{ type: '', institution: '', year: '', description: '' }] : academicAwards },
                  this.buildAndPublish
                )
              }}
            />

            {hasAcademicAward === 'Yes' && (
              <div style={{ paddingLeft: 16, borderLeft: '3px solid #6AA8FF', marginTop: 8 }}>
                <p style={{ fontWeight: 'bold', marginBottom: 8, color: '#3168CE' }}>
                  Academic Award Details
                </p>
                {academicAwards.map((award, index) => (
                  <div key={index} style={{ marginBottom: 12, padding: 10, background: '#f9f9f9', borderRadius: 6 }}>
                    <InputRow
                      rowName={`Award Type ${ index + 1 } `}
                      name='select'
                      value={award.type}
                      data={ACADEMIC_AWARD_OPTIONS}
                      required='*'
                      errMsg={error[`awardType_${index}`] || ''}
                      onChange={(val) => this.updateAward(index, 'type', val)}
                    />
                    <InputRow
                      rowName='Institution'
                      name='text'
                      value={award.institution}
                      required='*'
                      errMsg={error[`awardInstitution_${index}`] || ''}
                      onChange={(val) => this.updateAward(index, 'institution', val)}
                    />
                    <InputRow
                      rowName='Year'
                      name='date'
                      value={award.year}
                      required='*'
                      errMsg={error[`awardYear_${index}`] || ''}
                      onChange={(val) => this.updateAward(index, 'year', val)}
                    />
                    <InputRow
                      rowName='Description'
                      name='text'
                      value={award.description}
                      required={award.type === 'Other' ? '*' : ''}
                      errMsg={error[`awardDescription_${index}`] || ''}
                      onChange={(val) => this.updateAward(index, 'description', val)}
                    />
                    {academicAwards.length > 1 && (
                      <button
                        className='save'
                        style={{ background: '#e74c3c', fontSize: 12, padding: '4px 10px' }}
                        onClick={() => this.removeAward(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  className='save bordered-btn'
                  onClick={this.addAward}
                  style={{ marginTop: 6 }}
                >
                  + Add Another Award
                </button>
              </div>
            )}
          </div>

          {/* ---------- Coventry Alumni ---------- */}
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
              Are you a Coventry University graduate / alumni?
            </p>
            <InputRow
              rowName='Coventry Alumni'
              name='select'
              value={covGraduate}
              data={COV_ANSWER_OPTIONS}
              required='*'
              errMsg={error.covGraduate || ''}
              onChange={(val) => {
                this.setState({ covGraduate: val, coventryId: val === 'No' ? '' : coventryId }, this.buildAndPublish)
              }}
            />

            {covGraduate === 'Yes' && (
              <InputRow
                rowName='Coventry University ID Number'
                name='text'
                value={coventryId}
                required='*'
                errMsg={error.coventryId || ''}
                onChange={(val) => {
                  this.setState({ coventryId: val }, this.buildAndPublish)
                }}
              />
            )}
          </div>
        </div>

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGEducationHistory.propTypes = {
  onSave: PropTypes.func.isRequired,
  educationHistory: PropTypes.array,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  incompleteApplicationId: PropTypes.number
}

PGEducationHistory.defaultProps = {
  error: {},
  educationHistory: []
}
